// ===========================================
// Roster Student Pill
// ===========================================
// The avatar + first-name pill the teacher dashboard (Today's Classes) and the
// Teacher Tasks page both render per enrolled student. Extracted 2026-09-07 so
// ONE component carries the per-student status a teacher needs before class:
//
//   - "Away"   — a reported absence (AbsenceNotification) covering this
//                occurrence date. Category + who reported; never the free text
//                (Andrew D2). Read from the notification, not the Attendance row.
//   - marked   — the Attendance row's ABSENT / EXCUSED status (D3).
//
// Precedence: Away wins before the class (the reason is the useful part); once
// the date has passed the teacher's mark is the truth — ANY mark, so a student
// reported away who turned up and was marked PRESENT shows no badge at all
// (/qa 2026-09-07). No data → the plain pill, which is the NEUTRAL state — a
// failed fetch must never read as "everyone present".

import { getStudentAgeGroup } from '../../api/users.api';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CloseIcon from '@mui/icons-material/Close';
import CakeIcon from '@mui/icons-material/Cake';
import {
  characterAgeGroupFromName,
  getCharacterName,
  getCharacterColor,
} from '../brand/CharacterIllustration.utils';
import type { EnrolledStudentForAttendance, RosterAbsence } from '../../api/attendance.api';
import { getAttendanceStatusLabel } from '../../api/attendance.api';
import { describeAbsence } from '../../utils/absence';
import { toLocalDateString } from '../../utils/dateUtils';

export interface RosterStudentPillProps {
  student: {
    id: string;
    firstName: string;
    avatar?: string | null;
    /**
     * Either casing. The lessons API and the hybrid rows both send PascalCase
     * `AgeGroup` (`include: { AgeGroup: true }`); `ageGroup` is kept for callers
     * that map it. The value is the SCHOOL'S name for the group ("Pre-school",
     * "Adults"), not the brand enum — see `characterAgeGroupFromName`.
     */
    ageGroup?: { name?: string | null } | null;
    AgeGroup?: { name?: string | null } | null;
  };
  /** Per-student status for THIS occurrence (from the roster read). Optional. */
  status?: Pick<EnrolledStudentForAttendance, 'attendance' | 'absence'>;
  /** The occurrence's YYYY-MM-DD; decides whether the marked status outranks Away. */
  dateKey?: string;
  /**
   * A birthday in the current week. Rendered as a cake BESIDE THE NAME, never as
   * a badge (Andrew D6): a child can be away on their birthday, so a badge would
   * force a precedence answer between "Away" and "Birthday" that nobody wants.
   */
  birthday?: { dateKey: string; ageTurning: number; isToday: boolean } | null;
}

const MARKED_AWAY = new Set(['ABSENT', 'EXCUSED']);

/** Which badge to show, if any. Exported for the tests. */
export function resolveRosterBadge(
  status: RosterStudentPillProps['status'],
  dateKey?: string
): { kind: 'away'; absence: RosterAbsence } | { kind: 'marked'; label: string } | null {
  if (!status) return null;
  const isPast = !!dateKey && dateKey < toLocalDateString(new Date());
  const marked = status.attendance && MARKED_AWAY.has(status.attendance.status)
    ? status.attendance
    : undefined;
  // Past + any mark (PRESENT included): the mark is the truth, not the report.
  if (status.absence && !(isPast && status.attendance)) {
    return { kind: 'away', absence: status.absence };
  }
  if (marked) return { kind: 'marked', label: getAttendanceStatusLabel(marked.status) };
  return null;
}

export default function RosterStudentPill({ student, status, dateKey, birthday }: RosterStudentPillProps) {
  // A school's own name for the group, resolved to a character. Never cast the
  // raw name to `AgeGroup`: "Pre-school" and "Adults" are not keys, and the
  // cast crashed this pill — and every page rendering it — on 2026-09-08.
  const ageGroupName = characterAgeGroupFromName(getStudentAgeGroup(student)?.name);
  const characterName = getCharacterName(ageGroupName).toLowerCase();
  const characterColor = getCharacterColor(ageGroupName);
  const avatarSrc = student.avatar
    ? `/avatars/${student.avatar}`
    : `/avatars/${characterName}/${characterName}-happy.svg`;
  const badge = resolveRosterBadge(status, dateKey);
  // `Turns 9 today` / `Turns 9 on Thursday` — the weekday read with UTC getters,
  // because the dateKey is a @db.Date value at UTC midnight.
  const birthdayLabel = birthday
    ? birthday.isToday
      ? `Turns ${birthday.ageTurning} today`
      : `Turns ${birthday.ageTurning} on ${new Date(`${birthday.dateKey}T00:00:00.000Z`).toLocaleDateString('en-AU', { weekday: 'long', timeZone: 'UTC' })}`
    : null;

  const pill = (
    <Box
      data-testid={`roster-pill-${student.id}`}
      data-badge={badge?.kind ?? ''}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: characterColor,
        borderRadius: 3,
        pl: 0.25,
        pr: badge ? 0.5 : 1,
        py: 0.25,
      }}
      // Keyboard / screen-reader users reach the reason through the chip's
      // aria-label and the focusable pill; the Tooltip alone is mouse-only.
      tabIndex={badge?.kind === 'away' ? 0 : undefined}
    >
      <Box
        component="img"
        src={avatarSrc}
        alt={student.firstName}
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          bgcolor: 'white',
          border: `1.5px solid ${characterColor}`,
          objectFit: 'contain',
          p: 0.25,
          // Grey the avatar only — the white name on a light character colour
          // would drop below AA if the whole pill faded.
          opacity: badge ? 0.45 : 1,
        }}
      />
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'white', fontSize: '0.7rem' }}>
        {student.firstName}
      </Typography>
      {birthday && birthdayLabel && (
        <Tooltip title={birthdayLabel} arrow>
          <CakeIcon
            data-testid={`birthday-${student.id}`}
            aria-label={birthdayLabel}
            sx={{ fontSize: 14, color: 'white' }}
          />
        </Tooltip>
      )}
      {badge?.kind === 'away' && (
        <Chip
          icon={<EventBusyIcon />}
          label="Away"
          size="small"
          // Brand coral (#FFAE9E) via the theme's `warning` — an expected absence
          // is a heads-up, not an error (which is the red `marked` chip below).
          color="warning"
          aria-label={describeAbsence(badge.absence)}
          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 14 } }}
        />
      )}
      {badge?.kind === 'marked' && (
        <Chip
          icon={<CloseIcon />}
          label={badge.label}
          size="small"
          color="error"
          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 14 } }}
        />
      )}
    </Box>
  );

  if (badge?.kind === 'away') {
    return (
      <Tooltip title={describeAbsence(badge.absence)} arrow>
        {pill}
      </Tooltip>
    );
  }
  return pill;
}
